import { Col, Flex } from "@amzn/stencil-react-components/layout";
import { IconDocument, IconBin, IconCross, IconRefresh, IconUpload } from "@amzn/stencil-react-components/icons";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";
import React, { useEffect } from "react";
import { HVH_COLORS } from "../../../config/palette";
import {
  DOC_UPLOAD_STATUS_CARDS_NON_VALID_STATE,
  DOC_UPLOAD_STATUS_CARDS_VALID_STATE,
  EDM_REVIEW_STATUS,
} from "../../../utility/enums/common";
import { useChecklistArb } from "../../../hooks/use-arb";
import { Text } from "@amzn/stencil-react-components/text";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { DocStatusCards } from "../../../utility/types/common";
import { ModalContent, WithModal } from "@amzn/stencil-react-components/modal";
import { useAppDispatch, useAppSelector } from "../../../reduxStore/reduxHooks";
import {
  getSelectedFileType,
  removeFromDocStatusCards,
  updateDocStatusCardsFromEDM,
} from "../../../reduxStore/slices/docUploadSlice";
import { isApprovedByEDM, isErrorByEDM, isRejectedByEDM } from "../../../helpers/utils";
import { useCommitDocumentsMutation, usePreassignedLinkMutation } from "../../../reduxStore/api/apiSlice";
import { useParams } from "react-router-dom";
import { getConfigData } from "../../../reduxStore/slices/configSlice";

interface DocStatusCardsProps {
  doc: DocStatusCards;
  refetch: Function;
}

const DocStatusCardsItem = ({ doc, refetch }: DocStatusCardsProps) => {
  const { fileName, contentType, contentSize, state, documentId, reviewStatus, sha256, documentTypeId } = doc;
  const [commitDocumentsToEDM, { isLoading, isError }] = useCommitDocumentsMutation();
  const { applicationId } = useParams<{ applicationId: string }>();
  const { candidateData } = useAppSelector(getConfigData);
  const dispatch = useAppDispatch();
  const [
    getPreassignedLink,
    { data: preassignedLinkResponse, isSuccess: successPreassignedLink, isError: errorPreassignedLink },
  ] = usePreassignedLinkMutation();

  const { getMessage } = useChecklistArb();

  const handleDismissCard = () => {
    dispatch(removeFromDocStatusCards({ documentId }));
  };

  const handleDiscardCard = (callback: Function) => {
    dispatch(removeFromDocStatusCards({ documentId }));
    callback();
  };

  useEffect(() => {
    if (successPreassignedLink && preassignedLinkResponse) {
      dispatch(
        updateDocStatusCardsFromEDM(
          { documents: [{ ...doc, state: DOC_UPLOAD_STATUS_CARDS_VALID_STATE.SUCCESSFUL }] }
          // parseDocStatusCards({ files, preassignedLinkResponse, status: EDM_REVIEW_STATUS.APPROVED })
        )
      );
    }
    if (errorPreassignedLink) {
      dispatch(
        updateDocStatusCardsFromEDM(
          { documents: [{ ...doc, state: DOC_UPLOAD_STATUS_CARDS_NON_VALID_STATE.FAILED }] }
          // parseDocStatusCards({ files, preassignedLinkResponse, status: EDM_REVIEW_STATUS.ERROR })
        )
      );
    }
  }, [preassignedLinkResponse, errorPreassignedLink, successPreassignedLink]);

  const handleReuploadCard = () => {
    dispatch(
      updateDocStatusCardsFromEDM({ documents: [{ ...doc, state: DOC_UPLOAD_STATUS_CARDS_VALID_STATE.PROCESSING }] })
    );
    getPreassignedLink({
      applicationId,
      documentTypeId,
      candidateGlobalId: candidateData?.candidateGlobalId as string,
      files: [{ extension: contentType, filename: fileName, sha256 }],
    });
    refetch();
  };

  const handleRecommitCard = () => {
    dispatch(
      updateDocStatusCardsFromEDM({ documents: [{ ...doc, state: DOC_UPLOAD_STATUS_CARDS_VALID_STATE.PROCESSING }] })
    );
    commitDocumentsToEDM({
      applicationId,
      candidateGlobalId: candidateData?.candidateGlobalId as string,
      documents: {
        documentsMetadata: [
          {
            document_id: documentId,
            document_type_id: doc.documentTypeId,
            draft_id: doc.draftId,
          },
        ],
      },
    });
  };

  const isInFailedState = state in DOC_UPLOAD_STATUS_CARDS_NON_VALID_STATE && !isLoading;

  const renderCardDetail = () => {
    let docDetail = <></>;
    let actionButtons = <></>;

    // Setting up the documents details
    if (state === DOC_UPLOAD_STATUS_CARDS_VALID_STATE.PROCESSING || isLoading) {
      docDetail = (
        <>
          <IconDocument title={fileName} color={HVH_COLORS.PRIMARY_70} margin={{ top: 5, right: 5 }} />
          <Text fontSize={"T300"} color={HVH_COLORS.NEUTRAL_70}>
            {getMessage("Checklist-docUpload-status-card-uploading", "Uploading...")}
          </Text>
        </>
      );
    } else {
      docDetail = (
        <>
          <IconDocument title={fileName} color={HVH_COLORS.PRIMARY_70} margin={{ top: 5, right: 5 }} />
          <Flex flexDirection="column">
            <Text fontSize={"T300"} color={HVH_COLORS.NEUTRAL_90}>
              {fileName}
            </Text>
            <Text fontSize={"T100"} color={HVH_COLORS.NEUTRAL_70}>
              {contentType.toUpperCase()} {contentSize}
            </Text>
          </Flex>
        </>
      );
    }

    // Setting up the action buttons
    if (state === DOC_UPLOAD_STATUS_CARDS_VALID_STATE.SUCCESSFUL && !reviewStatus) {
      actionButtons = (
        <WithModal renderModal={renderModal} isScrollable={false} shouldCloseOnClickOutside={false}>
          {({ open }) => (
            <>
              <Button
                disabled={isLoading}
                aria-describedby="sroDialog"
                data-testid="deleteDocUploadCard"
                onClick={open}
                icon={<IconBin color={HVH_COLORS.RED_70} title="Discard" />}
                variant={ButtonVariant.Tertiary}
              />
              <span hidden id="sroDialog">
                has pop-up dialog
              </span>
            </>
          )}
        </WithModal>
      );
    } else if (state === DOC_UPLOAD_STATUS_CARDS_NON_VALID_STATE.FAILED && !reviewStatus) {
      actionButtons = (
        <>
          <Button
            disabled={isLoading}
            icon={<IconCross color={HVH_COLORS.PRIMARY_70} title="Close" />}
            onClick={handleDismissCard}
            variant={ButtonVariant.Tertiary}
          />
          <Button
            disabled={isLoading}
            icon={<IconRefresh color={HVH_COLORS.PRIMARY_70} title="Reupload file" />}
            onClick={isErrorByEDM(reviewStatus) ? handleRecommitCard : handleReuploadCard}
            variant={ButtonVariant.Tertiary}
          />
        </>
      );
    }

    return (
      <Flex flexDirection="row" alignItems="center" justifyContent="space-between">
        <Flex flexDirection="row">{docDetail}</Flex>
        <Flex flexDirection="row">{actionButtons}</Flex>
      </Flex>
    );
  };

  const renderDocStateDescription = () => {
    const isValidToRender = isInFailedState || isError || reviewStatus;
    let message = "";
    let status = Status.Negative;

    if (isValidToRender) {
      if (isApprovedByEDM(reviewStatus)) {
        message = getMessage("Checklist-docUpload-status-card-approved", "Document Approved");
        status = Status.Positive;
      } else if (isRejectedByEDM(reviewStatus)) {
        message = getMessage("Checklist-docUpload-status-card-rejected", "Document Rejected");
        status = Status.Warning;
      } else if (isErrorByEDM(reviewStatus)) {
        message = getMessage("Checklist-docUpload-status-card-error", "Error when commiting Document");
      } else if (state === DOC_UPLOAD_STATUS_CARDS_NON_VALID_STATE.FAILED) {
        message = getMessage("Checklist-docUpload-status-card-error-failed-upload", "Failed upload, please try again");
      }
    }
    return isValidToRender && <StatusIndicator messageText={message} status={status} />;
  };

  const renderModal = ({ close }: { close: () => void }) => (
    <ModalContent
      data-modal-type="upload-doc-delete"
      titleText={getMessage("Checklist-docUpload-status-card-modal-header", "Delete document?")}
      buttons={[
        <Button
          key={`Checklist-docUpload-modal-cancel-${documentId}`}
          onClick={close}
          variant={ButtonVariant.Secondary}
        >
          <Text fontWeight={"regular"}>{getMessage("Checklist-docUpload-status-card-modal-cancel", "No, cancel")}</Text>
        </Button>,
        <Button
          key={`Checklist-docUpload-modal-delete-${documentId}`}
          onClick={() => handleDiscardCard(close)}
          variant={ButtonVariant.Primary}
          isDestructive
        >
          <Text fontWeight={"regular"}>
            {getMessage("Checklist-docUpload-status-card-modal-continue", "Yes, delete")}
          </Text>
        </Button>,
      ]}
    >
      <Text>
        {getMessage(
          "Checklist-docUpload-status-card-modal-description",
          "Are you sure you would like to delete this document and all of its attachments?"
        )}
      </Text>
    </ModalContent>
  );

  return (
    <Col gridGap="S100">
      <Col padding="S300" gridGap="S300" backgroundColor={isInFailedState ? HVH_COLORS.RED_10 : HVH_COLORS.NEUTRAL_05}>
        {renderCardDetail()}
      </Col>
      {renderDocStateDescription()}
    </Col>
  );
};

export default DocStatusCardsItem;
